/**
 * CousinDiscoveryFeed — public homepage widget for IndianFamilyTree.com.
 * Renders the 10 most recent notable ancestor-matches so anonymous
 * visitors can see the network effect in real time.
 *
 * Powered by: GET /api/ancestor-matches/public/highlights
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpPublic } from "@/lib/api";
import { Users, Sparkles, Loader2 } from "lucide-react";

export default function CousinDiscoveryFeed({ palette: P, siteScope = "indianfamilytree" }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    httpPublic.get("/ancestor-matches/public/highlights", { params: { limit: 10, site: siteScope } })
      .then((r) => alive && setItems(r.data?.items || []))
      .catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, [siteScope]);

  if (items === null) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-8 text-center opacity-60"
               data-testid="ift-cousin-feed-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </section>
    );
  }
  if (items.length === 0) return null; // hide silently when empty

  return (
    <section className="max-w-6xl mx-auto px-4 py-10" data-testid="ift-cousin-feed">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4" style={{ color: P.saffron }} />
        <h2 className="font-display text-2xl">Cousins discovering each other</h2>
      </div>
      <p className="text-xs mb-4" style={{ color: P.inkSoft }}>
        Live from the network — every time our engine spots two trees with
        the same ancestor, we surface it here so lost cousins can find each
        other faster.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((it) => (
          <div
            key={it.id}
            className="rounded-xl border bg-white p-4"
            style={{ borderColor: P.saffronSoft }}
            data-testid={`ift-cousin-${it.id}`}
          >
            <div className="flex items-start gap-2">
              <span
                className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full"
                style={{
                  background: it.strength === "exact" ? "#FEF3C7" : "#EEF2FF",
                  color: it.strength === "exact" ? "#92400E" : "#3730A3",
                }}
              >
                {it.strength === "exact" ? "exact match" : "strong match"}
              </span>
              {it.state && (
                <span className="text-[10px] font-mono opacity-60 ml-auto">{it.state}</span>
              )}
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center mt-3">
              <Link
                to={`/family/${it.left.tree_slug}`}
                className="min-w-0 group"
              >
                <div className="text-[10px] uppercase font-mono opacity-60 truncate">
                  {it.left.tree_name}
                </div>
                <div className="font-semibold truncate group-hover:underline"
                     style={{ color: P.ink }}>
                  {it.left.surname} {it.left.year ? `· b. ${it.left.year}` : ""}
                </div>
              </Link>
              <div className="text-center text-xs opacity-50">↔</div>
              <Link
                to={`/family/${it.right.tree_slug}`}
                className="min-w-0 text-right group"
              >
                <div className="text-[10px] uppercase font-mono opacity-60 truncate">
                  {it.right.tree_name}
                </div>
                <div className="font-semibold truncate group-hover:underline"
                     style={{ color: P.ink }}>
                  {it.right.surname} {it.right.year ? `· b. ${it.right.year}` : ""}
                </div>
              </Link>
            </div>
            {it.shared_places?.length > 0 && (
              <div className="text-[11px] font-mono opacity-60 mt-3">
                shared: {it.shared_places.slice(0, 3).join(", ")}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="text-center mt-4">
        <Link
          to="/kin-network"
          className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
          style={{ color: P.saffron }}
          data-testid="ift-kin-network-link"
        >
          <Users className="w-4 h-4" /> See the full Kin Network
        </Link>
      </div>
    </section>
  );
}
